import { Button, cn } from "@nextui-org/react";
import { faChevronUp, faCircleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import BookingCurrent from "@/components/bookings/current/BookingCurrent";
import { useBookingsCurrentStore } from "@/store/BookingsCurrentStore";
import { anyBookingCheckedIn, anyBookingLateOrMissed, getDayName } from "@/lib/helpers";
import { IBooking } from "@/lib/types";

// ---------------------- Props

interface BookingDayCurrentProps {
  day: number;
  dayOfMonth: number;
  bookings: IBooking[];
}

// ---------------------- Component

const BookingDayCurrent = ({ day, dayOfMonth, bookings }: BookingDayCurrentProps) => {
  // --------- Variables / Hooks

  const { isDayExpanded, setExpandedDay } = useBookingsCurrentStore();

  const amIExpanded = isDayExpanded(day);

  const haveBookings = bookings.length > 0;

  const checkedIn = anyBookingCheckedIn(bookings);

  const hasLateOrMissed = anyBookingLateOrMissed(bookings);

  // --------- JSX

  return (
    <>
      <div
        className={cn(
          "flex h-auto w-full flex-col items-center justify-start rounded-lg border border-gray-200 transition-transform duration-500 ease-in-out dark:border-gray-800",
          [
            !amIExpanded && "opacity-80",
            amIExpanded &&
              "my-1 border-gray-300 bg-gray-100/20 dark:border-gray-700 dark:bg-gray-800/10",
          ],
        )}
      >
        <div className="w-full">
          <div
            className="flex h-auto w-full flex-row items-center justify-between px-3 py-1"
            onClick={() => setExpandedDay(day)}
          >
            {/*******************************************/}
            {/* Date Info */}
            {/*******************************************/}

            <div className="flex h-auto w-full flex-row items-center justify-start xs:gap-3 sm:gap-5">
              <p className="w-7 text-sm font-semibold text-gray-600 dark:text-gray-300 sm:text-base">
                {dayOfMonth}
              </p>
              <p className="text-base font-medium text-gray-800 dark:text-gray-200 sm:text-lg">
                {getDayName(day)}
              </p>
            </div>

            <div className="-mr-3 flex h-auto w-full flex-row items-center justify-end">
              {/*******************************************/}
              {/* Issues Icon */}
              {/*******************************************/}

              {hasLateOrMissed && (
                <Button
                  isIconOnly
                  radius="full"
                  className="bg-transparent p-2 text-current transition-all duration-200"
                >
                  <FontAwesomeIcon
                    icon={faCircleExclamation}
                    className="h-4 text-[#ff321c] transition-transform duration-500"
                  />
                </Button>
              )}

              {/*******************************************/}
              {/* Booking Count Indicator */}
              {/*******************************************/}

              <div
                className={cn(
                  "relative flex h-6 w-6 flex-col items-center justify-center rounded-full bg-gray-800/70 pl-[0.025rem] pt-[0.1rem] text-xs font-bold text-white",
                  !haveBookings && "bg-gray-800/70",
                  checkedIn && "bg-[#0cb307]",
                )}
              >
                {checkedIn ? (
                  <>
                    <span className="absolute -mt-[0.15rem] inline-flex h-6 w-6 animate-ping rounded-full bg-[#0cb307] opacity-65"></span>
                    <span className="relative">{bookings.length}</span>
                  </>
                ) : (
                  <span>{bookings.length}</span>
                )}
              </div>

              {/*******************************************/}
              {/* Show/Hide Button */}
              {/*******************************************/}

              <Button
                isIconOnly
                radius="full"
                className="bg-transparent p-2 text-current transition-all duration-200"
                onPress={() => setExpandedDay(day)}
              >
                <FontAwesomeIcon
                  icon={amIExpanded ? faChevronUp : faChevronDown}
                  className="h-3 text-gray-500 transition-transform duration-500 xs:h-4"
                />
              </Button>
            </div>
          </div>

          {/*******************************************/}
          {/* Bookings List */}
          {/*******************************************/}

          <div
            className={cn(
              "overflow-hidden transition-all duration-500",
              amIExpanded ? "opacity-100" : "h-0 opacity-0",
            )}
          >
            <div className="flex flex-col gap-2 p-2 pt-1">
              {haveBookings ? (
                bookings.map((booking) => (
                  <div
                    key={booking.bookingId}
                    className="w-full"
                  >
                    <BookingCurrent
                      day={day}
                      booking={booking}
                    />
                  </div>
                ))
              ) : (
                <p className="mb-2 ml-1 text-sm text-gray-600 dark:text-gray-400 sm:text-base">
                  No bookings today.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingDayCurrent;
